import { useFormik } from 'formik'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { Form } from 'sutra/components/Form'
import * as Yup from 'yup'

import { handleException, runOperation } from 'clients/exception-handling'
import { Loader } from 'components'
import { NewProject, Selector } from 'features/project-form/components'
import {
  ModalHeader,
  ModalWrapper,
} from 'features/project-form/components/modal'
import { ValidationProgress } from 'features/project-form/components/validation-progress'
import { CsvForm } from 'features/project-form/features/csv/CsvForm'
import { ProductForm } from 'features/project-form/features/product/ProductForm'
import { Product } from 'features/project-form/features/product/types'
import { SourceForm } from 'features/project-form/features/source'
import {
  ProjectSourceFile,
  ProjectSourceURL,
} from 'features/project-form/features/source/types'
import { useProjectFormService } from 'features/project-form/service'
import { validateProjectSourcesWithProgress } from 'features/project-form/service/validate-with-progress'
import type { ValidatePhaseEvent } from 'features/project-form/types'
import type { ProjectDataType, ProjectUsage } from 'features/projects/constants'

const layout = { labelCol: { span: 5 } }

type Props = {
  handleClose: () => void
  reservedKeywords: string[]
}

export type FormValues = {
  title: string
  description: string
  products: Array<Product>
  sources: Array<ProjectSourceFile | ProjectSourceURL>
  bypassValidations: boolean
  usage: ProjectUsage | null
  data_types: ProjectDataType[]
  is_internal: boolean | null
  account_uuid: string | null
}

const initialValues: FormValues = {
  title: '',
  description: '',
  products: [],
  sources: [],
  bypassValidations: false,
  usage: null,
  data_types: [],
  is_internal: null,
  account_uuid: null,
}

const NewProjectSchema = Yup.object().shape({
  title: Yup.string()
    .required('A project title is required')
    .min(2, '2 Character Minimum for Title')
    .max(100, '100 Character Max for Title'),
  description: Yup.string()
    .min(2, '2 Character Minimum for Description')
    .max(100, '100 Character Max for Description'),
  usage: Yup.string().nullable().required('A usage is required'),
  data_types: Yup.array().min(1, 'At least one data type is required'),
  is_internal: Yup.boolean().nullable().required('A visibility is required'),
})

// Helper function to upload files and update their keys
const handleFileUploads = async (
  sources: Array<ProjectSourceFile | ProjectSourceURL>,
  projectFormService: ReturnType<typeof useProjectFormService>,
): Promise<void> => {
  const fileSources = sources.filter(
    (source): source is ProjectSourceFile => 'file' in source && !!source.file,
  )

  // s3 paths are returned from upload endpoint
  const paths = await Promise.all(
    fileSources.map(({ file }) => projectFormService.uploadFile(file)),
  )

  // we need to replace the key with the returned path
  fileSources.forEach((source, index) => {
    source.key = paths[index]!.data.path
  })
}

export const ProjectNewForm = (props: Props): JSX.Element => {
  const projectFormService = useProjectFormService()
  const [page, setPage] = useState<string>('selector')
  const [isEmptyProject, setIsEmptyProject] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [validatePhase, setValidatePhase] = useState<ValidatePhaseEvent | null>(
    null,
  )
  const [stopWords, setStopWords] = useState<string[]>([])
  const [sourceToEditIndex, setSourceToEditIndex] = useState<null | number>(
    null,
  )
  const [productToEditIndex, setProductToEditIndex] = useState<null | number>(
    null,
  )

  const formik = useFormik({
    initialValues,
    validationSchema: NewProjectSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (
      {
        title,
        description,
        sources,
        products,
        usage,
        data_types,
        is_internal,
        account_uuid,
      },
      { setFieldError },
    ) => {
      try {
        setLoading(true)

        if (isEmptyProject) {
          await projectFormService.createProject(title, description, {
            usage,
            data_types,
            is_internal,
            account_uuid,
          })
          toast.success(`Project ${title} created!`)
          setLoading(false)
          props.handleClose()
          return
        }

        if (sources.length) {
          if (sources.some((source) => !source.product_id)) {
            setFieldError(
              'sources',
              'Every source must be assigned to a product.',
            )
            return
          }
          await handleFileUploads(sources, projectFormService)

          if (!formik.values.bypassValidations) {
            setValidatePhase(null)
            try {
              await validateProjectSourcesWithProgress({
                service: projectFormService,
                payload: {
                  sources,
                  products: products,
                  project_title: title,
                  project_uuid: null,
                },
                onPhase: setValidatePhase,
              })
            } finally {
              setValidatePhase(null)
            }
            toast.success('Validations passed. Uploading...')
          }

          const { data: project } = await projectFormService.createProject(
            title,
            description,
            { usage, data_types, is_internal, account_uuid },
          )
          const connected = await runOperation(
            () =>
              projectFormService.setProjectSources({
                sources,
                products,
                stop_words: stopWords,
                proj_uuid: project.proj_uuid,
                project_title: title,
              }),
            {
              operation: 'project.sources.connect',
              context: { project_id: project.proj_uuid },
            },
          )
          if (connected.status !== 'succeeded') {
            toast.warn(
              `Project ${title} was created, but its sources were not connected. Open the project to add them; do not create another project.`,
            )
            props.handleClose()
            return
          }
          toast.success(`Project ${title} created!`)
          setLoading(false)
          props.handleClose()
        }
      } catch (e: unknown) {
        handleException(e, {
          operation: 'project.create',
          disposition: 'failed',
        })

        setLoading(false)
        toast.error(
          (e as { response?: { data?: { detail?: string } }; message?: string })
            ?.response?.data?.detail || (e as Error).message,
        )
      } finally {
        setLoading(false)
      }
    },
  })

  const onBypassValidationsChange = (checked: boolean): void => {
    formik.setFieldValue('bypassValidations', checked)
  }

  const openNewProject = useCallback(() => setPage('project'), [])
  const openNewSource = useCallback(async () => {
    formik.setTouched({
      title: true,
      description: true,
      usage: true,
      data_types: true,
      is_internal: true,
    })
    const response = await formik.validateForm()

    if (!Object.values(response).length) {
      setPage('source')
    }
  }, [formik])

  const addProduct = useCallback(
    (product: Product) => {
      if (productToEditIndex !== null) {
        formik.values.products[productToEditIndex] = product
        formik.setFieldValue('products', formik.values.products)
        setProductToEditIndex(null)
      } else {
        formik.setFieldValue('products', [...formik.values.products, product])
      }
    },
    [productToEditIndex, formik],
  )
  const deleteProduct = useCallback(() => {
    if (productToEditIndex !== null) {
      openNewProject()
      formik.setFieldValue(
        'products',
        formik.values.products.filter(
          (_, index) => index !== productToEditIndex,
        ),
      )
      setProductToEditIndex(null)
    }
  }, [productToEditIndex, formik, openNewProject])
  const addSource = useCallback(
    (source: ProjectSourceFile | ProjectSourceURL) => {
      if (sourceToEditIndex !== null) {
        formik.values.sources[sourceToEditIndex] = source
        formik.setFieldValue('sources', formik.values.sources)
        setSourceToEditIndex(null)
      } else {
        formik.setFieldValue('sources', [...formik.values.sources, source])
      }
    },
    [sourceToEditIndex, formik],
  )
  const deleteSource = useCallback(() => {
    if (sourceToEditIndex !== null) {
      openNewProject()
      formik.setFieldValue(
        'sources',
        formik.values.sources.filter((_, index) => index !== sourceToEditIndex),
      )
      setSourceToEditIndex(null)
    }
  }, [sourceToEditIndex, formik, openNewProject])

  const isSelector = page === 'selector'
  const isCsvUpload = page === 'csv'
  const isProject = page === 'project'
  const isProduct = page === 'product'
  const isSource = page === 'source'

  const handleValidateOnly = async (): Promise<void> => {
    const { title, sources, products } = formik.values
    if (sources.length) {
      setLoading(true)
      if (sources.some((source) => !source.product_id)) {
        formik.setFieldError(
          'sources',
          'Every source must be assigned to a product.',
        )
        return
      }
      await handleFileUploads(sources, projectFormService)
      try {
        setLoading(true)
        setValidatePhase(null)
        await validateProjectSourcesWithProgress({
          service: projectFormService,
          payload: {
            sources,
            products,
            project_title: title,
            project_uuid: null,
          },
          onPhase: setValidatePhase,
        })
        toast.success('Validations passed.')
      } catch (e) {
        handleException(e, {
          operation: 'project.create.validate',
          disposition: 'failed',
        })

        toast.error(
          (e as Error & { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ||
            (e as Error).message ||
            'An Error Occurred',
        )
      } finally {
        setValidatePhase(null)
        setLoading(false)
      }
    } else {
      toast.error('No sources to validate.')
    }
  }

  return (
    <ModalWrapper>
      <ModalHeader
        handleClose={props.handleClose}
        handleBack={
          isSource || isProduct
            ? openNewProject
            : isCsvUpload
              ? () => setPage('selector')
              : undefined
        }
      />
      <Wrapper>
        {isSelector && (
          <Selector
            onBlankClick={() => setPage('project')}
            onCSVClick={() => setPage('csv')}
            onEmptyClick={() => {
              setIsEmptyProject(true)
              setPage('project')
            }}
          />
        )}
        {isProject && (
          <Form layout="horizontal" onFinish={formik.submitForm} {...layout}>
            <NewProject
              sources={formik.values.sources}
              products={formik.values.products}
              handleOpenSource={openNewSource}
              handleOpenProduct={() => setPage('product')}
              formik={formik}
              isSubmitDisabled={
                isEmptyProject ? false : !formik.values.sources.length
              }
              isEmptyProject={isEmptyProject}
              stopWords={stopWords}
              setStopWords={setStopWords}
              setSourceToEditIndex={setSourceToEditIndex}
              setProductToEditIndex={setProductToEditIndex}
            />
          </Form>
        )}
        {isProduct && (
          <ProductForm
            addProduct={addProduct}
            productToEdit={
              productToEditIndex !== null
                ? (formik.values.products[productToEditIndex] ?? null)
                : null
            }
            handleOpenProject={openNewProject}
            deleteProduct={deleteProduct}
            setProductToEditIndex={setProductToEditIndex}
          />
        )}
        {isSource && (
          <SourceForm
            products={formik.values.products}
            reservedKeywords={props.reservedKeywords}
            sourceNames={formik.values.sources.map((source) => source.title)}
            handleOpenProject={openNewProject}
            addSource={addSource}
            sourceToEdit={
              sourceToEditIndex !== null
                ? (formik.values.sources[sourceToEditIndex] ?? null)
                : null
            }
            sourceToEditIndex={sourceToEditIndex}
            setSourceToEditIndex={setSourceToEditIndex}
            deleteSource={deleteSource}
          />
        )}
        {isCsvUpload && (
          <CsvForm
            reservedKeywords={props.reservedKeywords}
            onSuccess={({ sources: sourceList, products: productList }) => {
              setPage('project')
              formik.setFieldValue('sources', [
                ...formik.values.sources,
                ...sourceList,
              ])
              formik.setFieldValue('products', [
                ...formik.values.products,
                ...productList,
              ])
            }}
          />
        )}
        <ValidateOnlyButton
          variant="primary"
          type="submit"
          onClick={handleValidateOnly}
          disabled={!formik.isValid || formik.isSubmitting}
        >
          Validate Only
        </ValidateOnlyButton>
        <Checkbox onChange={onBypassValidationsChange}>
          Bypass validations
        </Checkbox>
        {loading && (
          <Overlay>
            {validatePhase !== null ? (
              <ValidationProgress event={validatePhase} />
            ) : (
              <Loader />
            )}
          </Overlay>
        )}
      </Wrapper>
    </ModalWrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
`
const Overlay = styled.div`
  background: rgba(255, 255, 255, 0.7);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ValidateOnlyButton = styled(Button)`
  margin-right: 12px;
`
